import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Divider, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { ClientResponse, IQueryPerams } from "../../config/types";
import CustomizedSwitch from "../../components/Switch";
import { formatSnakeCaseToReadable } from "../../utils/common";
import { DomainSelectInput } from "../select-input-models/DomainSelectInput";
import { Field } from "../../utils/formSchema";
import { dmsDomainsNamesTy } from "../../config/constants/domain";
import {
  distributionBatchStatusList,
  DistributionBatchType
} from "../../config/constants/common";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import { useAppSelector } from "../../store/hooks";
import { systemUser } from "../../config/constants/system-users";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T) => void;
  isDeleted?: boolean;
  status?: boolean;
  default_status?:DistributionBatchType
  to_location_name?:string
}

interface Ifilter {
  from_location_id: string;
  from_location_name?: string;

  to_location_id: string;
  to_location_name?: string;
  distribution_batch_status?: DistributionBatchType;
  is_deleted?: boolean;
}
const distributionStatusList = distributionBatchStatusList.map((item) => ({
  label: item,
  value: item
}));

export const AssetDistributionLogsFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet,
  status = true,
  isDeleted=false,
  default_status
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);
  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<Ifilter>({
      from_location_id: "",
      to_location_id: "",
      ...(default_status && {distribution_batch_status:default_status})
  
    });
    const currentUser=useAppSelector((data)=>data.authVerify)
    const isSuperAdmin=currentUser.role===systemUser.SUPER_ADMIN
  const resetFilter = <K extends keyof Ifilter>(key: K) => {
    updateForm((p) => {
       const value= typeof (p[key]?.value)=== 'boolean' ? false :""
      const state = {
        ...p,
        [key]: { value },
        ...(
          isSuperAdmin &&
          {
            from_location_id: {
              value: key === "from_location_name" ? "" : p.from_location_id.value
            },
            to_location_id: {
              value: key === "to_location_name" ? "" : p.to_location_id.value
            }
          }
        )

      };
      updateQuerySet((prev) => ({ ...prev, [key]: value }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();

    updateQuerySet(data);
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ 
    form.from_location_id.value,
    form.to_location_id.value,
    form.is_deleted?.value,
    form.distribution_batch_status?.value
  ]);

  function handleToDomainSelect(
    item: Field,
    domain: dmsDomainsNamesTy,
    label?: string
  ) {
    updateForm((p) => ({
      ...p,
      to_location_id: item,
      to_location_name: { value: label }
    }));
  }

  function handleFromDomainSelect(
    item: Field,
    domain: dmsDomainsNamesTy,
    label?: string
  ) {
    updateForm((p) => ({
      ...p,
      from_location_id: item,
      from_location_name: { value: label }
    }));
  }

  const getFilteredFormValues = () => {
    const { from_location_id, to_location_id, ...filteredValues } =
      getFormValues();
    return filteredValues;
  };
  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      width={"100%"}
      mb={1}
    >
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading}>
          <Stack direction={"column"} spacing={2}>
            {status && (
              <ItemSelectInput
                size="full"
                label={"Distribution Status"}
                item={""}
                placeholder={"search by distribution status ..."}
                items={distributionStatusList}
                handleChange={updateInputField("distribution_batch_status")}
                value={form.distribution_batch_status?.value ?? ""}
              />
            )}
            {
              isSuperAdmin &&
              <>
                          <DomainSelectInput
              direction="column"
              label="Distributor  Domain Name"
              placeholder="Select Distributor  Domain Name"
              handleChange={handleFromDomainSelect}
              value={{ domain_id: form.from_location_id.value }}
            />

            <Divider orientation="horizontal" />
            <DomainSelectInput
              direction="column"
              placeholder="Select Recipent Domain Name"
              label="Recipent Domain Name"
              handleChange={handleToDomainSelect}
            />

              </>
            }

            {isDeleted  && (
              <CustomizedSwitch
                title="Deleted"
                onChange={updateInputField("is_deleted", "BOOLEAN")}
                value={form.is_deleted?.value ?? false}
              />
            )}
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFilteredFormValues())
            .filter(
              ([_, value]) =>
                (typeof value === "string" && value.trim() !== "") ||
                typeof value === "boolean"
            )
            .map(([key, value]) => {
              return  (<Box key={key}>
              { status && 
               <FilterItemButton
                  label={`By ${formatSnakeCaseToReadable(key)}`}
                  tip={value.toString()}
                  height={40}
                  onClick={() => resetFilter(key as keyof Ifilter)}
                />
              }
              </Box>

              )
            })}
        </Box>
      </Stack>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570
    }
  })
};
